import { defineStore } from 'pinia'
import { useAuthStore } from '@sto/auth.store'
import * as AssetService from '@ser/asset'
import * as SaleService from '@ser/sale'
import {
    acceptProvisionalOffer,
    addMedia,
    assignLotNumber,
    generateDescription,
    getSaleInvoiceDefaults
} from "@ser/asset/api.js";
import * as VehicleService from "@ser/vehicle/index.js";
import {shallowRef} from "vue";
import GeneralAssetCreateStepInfo from "@par/asset/GeneralAssetCreateStepInfo.vue";
import GeneralAssetCreateStepImages from "@par/asset/GeneralAssetCreateStepImages.vue"
import GeneralAssetCreateStepSale from "@par/asset/GeneralAssetCreateStepSale.vue"
import GeneralAssetCreateStepValuation from "@par/asset/GeneralAssetCreateStepValuation.vue"
import {deleteAllForAsset, returnToSeller, updateConnected} from "@ser/asset";

export const useAssetStore = defineStore('asset', {

    state: () => {
        return {
            media: {},
            transfer_orders: {},
            transfer_requests: {},
            _assign_seller_modal_active: false,
            _asset: null,
            _general_asset_create_steps: [
                {
                    id: 'info',
                    name: 'Information',
                    description: 'Basic information about the item',
                    complete: false,
                    status: 'current',
                    component: shallowRef(GeneralAssetCreateStepInfo)
                },
                {
                    id: 'valuation',
                    name: 'Values',
                    description: '(Optional) Item values',
                    complete: false,
                    status: 'upcoming',
                    component: shallowRef(GeneralAssetCreateStepValuation)
                },
                {
                    id: 'images',
                    name: 'Images',
                    description: '(Optional) Add some images of the item',
                    complete: false,
                    status: 'upcoming',
                    component: shallowRef(GeneralAssetCreateStepImages)
                },
                {
                    id: 'sale',
                    name: 'Sale',
                    description: '(Optional) Sale settings',
                    complete: false,
                    status: 'upcoming',
                    component: shallowRef(GeneralAssetCreateStepSale)
                }
            ],
            _active_general_asset_create_step_id: 'info',
            _asset_error_heading: '',
            _asset_error_message: '',
            _create_steps_complete_base: false,
            _asset_loaded: false,
            _asset_id: null,
            _asset_create_drawer_open: false,
            _asset_create_drawer_mode: null
        }
    },

    getters: {
        asset: state => state._asset,
        general_asset_create_steps: state => state._general_asset_create_steps,
        current_active_general_asset_create_step: state => {
            let current_step_index = state._general_asset_create_steps.findIndex(s => s.status == 'current')
            current_step_index = current_step_index ?? state.create_steps_base - 1
            return state._general_asset_create_steps[current_step_index].id

        },
        active_general_asset_create_step_id: state => state.active_general_asset_create_step_id,
        asset_error_heading: state => state._asset_error_heading,
        asset_error_message: state => state._asset_error_message,
        asset_media: state => {
            return state.media
        },
        assign_seller_modal_active: state => state._assign_seller_modal_active,
        create_steps_complete: state => state._create_steps_complete_base,
        asset_loaded: state => state._asset_loaded,
        asset_id: state => state._asset_id,
        asset_create_drawer_open: state => state._asset_create_drawer_open,
        asset_create_drawer_mode: state => state._asset_create_drawer_mode
    },

    actions: {

        resetStore() {

            this.media = {}
            this.transfer_orders = {}
            this.transfer_requests = {}
            this._assign_seller_modal_active = false
            this._asset = null
            this._active_general_asset_create_step_id = 'foo'
            this._asset_error_heading = ''
            this._asset_error_message = ''
            this._asset_loaded = false
            this._general_asset_create_steps.forEach((step, index) => {
                step.status = index === 0 ? 'current' : 'upcoming'
                step.complete = false
            })
            this._create_steps_complete_base = false
        },

        setCreateModalState(open) {
            this._create_steps_complete_base = open
        },

        /**
         * activate a specific step
         *
         * @param {str} step_id for the target step
         */
        activateStep(step_id) {
            const current_step_index = this._general_asset_create_steps.findIndex(s => s.status == 'current')
            this._general_asset_create_steps[current_step_index].status = this._general_asset_create_steps[current_step_index].complete ? 'complete' : 'upcoming'
            const target_step_index = this._general_asset_create_steps.findIndex(s => s.id == step_id)
            if (!isNaN(target_step_index) && target_step_index >= 0) this._general_asset_create_steps[target_step_index].status = 'current'
        },

        /**
         * activate tne next step in the arrray
         */
        activateNextStep() {
            const current_step_index = this._general_asset_create_steps.findIndex(s => s.status == 'current')
            // complete this step
            this._general_asset_create_steps[current_step_index].complete = true
            this._general_asset_create_steps[current_step_index].status = 'complete'
            // not the final step
            if (current_step_index < this._general_asset_create_steps.length) {
                //this.active_step_id = this.create_steps_base[current_step_index + 1].id
                this._general_asset_create_steps[current_step_index + 1].status = 'current'
            } else {
                // TODO handle final step sceanrio
            }
        },

        /**
         * activate the previous step
         */
        activatePrevStep() {
            const current_step_index = this._general_asset_create_steps.findIndex(s => s.status == 'current')
            // not the first step
            if (current_step_index > 0) {
                this._general_asset_create_steps[current_step_index].status = this._general_asset_create_steps[current_step_index].complete ? 'complete' : 'upcoming'
                this._general_asset_create_steps[current_step_index - 1].status = 'current'
            }
        },

        /**
         * create a generic asset
         * @param payload
         * @returns {Promise<obj>}
         */
        async create(payload) {
            return AssetService.create(payload)
        },

        /**
         * update an asset
         */
        async update(asset_id, payload) {
            return AssetService.update(asset_id, payload)
        },

        /**
         * get an asset
         */
        async show(asset_id) {
            return AssetService.show(asset_id)
        },

        async showAndSetStore(asset_id) {
            const asset = await AssetService.show(asset_id)
            if (this._asset?.id && (this._asset?.id != asset_id)) this.resetStore()

            try {
                this.clearAssetError()
                const asset = await this.show(asset_id)
                this.setGeneralAssetStepsState(asset)
                this._asset = asset
                this._asset_loaded = true

            } catch (error) {
                console.error('showAndSetStore error', error)
            }
        },

        setGeneralAssetStepsState(asset) {

        },

        clearAssetError() {
            this.setAssetError()
        },

        /**
         * set an asset error
         */
        setAssetError(heading, message) {
            this._asset_error_heading = heading || ''
            this._asset_error_message = message || ''
        },

        assign_seller_modal_open() {
            this._assign_seller_modal_active = true
        },

        assign_seller_modal_close() {
            this._assign_seller_modal_active = false
        },

        /**
         * assign an account(s) to an asset
         */
        async assignAccount(asset_id, data) {
            return AssetService.assignAccount(asset_id, data)
        },

        /**
         * assign a sale code to a given asset id
         */
        async assignSaleCode(auctioneer_id, asset_id, data) {
            return SaleService.assignSaleCode(auctioneer_id, asset_id, data)
        },

        /**
         * store a feature value
         */
        async storeFeatureValue(asset_id, data) {
            return AssetService.storeFeatureValue(asset_id, data)
        },

        /**
         * update a feature value
         */
        async updateFeatureValue(asset_id, feature_value_id, data) {
            return AssetService.updateFeatureValue(asset_id, feature_value_id, data)
        },

        /**
         * create a new link and attach to this asset
         */
        async addNewLink(asset_id, data) {
            return AssetService.addNewLink(asset_id, data)
        },

        /**
         * get the sale history for an asset
         */
        async saleHistory(asset_id) {
            return AssetService.saleHistory(asset_id)
        },

        /**
         * create a provisional offer
         */
        async createProvisionalOffer(asset_id, lot_id, data) {
            return AssetService.createProvisionalOffer(asset_id, lot_id, data)
        },

        /**
         * accept a provisional offer
         */
        async acceptProvisionalOffer(asset_id, lot_id, bid_id) {
            return AssetService.acceptProvisionalOffer(asset_id, lot_id, bid_id)
        },

        /**
         * reject a provisional offer
         */
        async rejectProvisionalOffer(asset_id, lot_id, bid_id) {
            return AssetService.rejectProvisionalOffer(asset_id, lot_id, bid_id)
        },

        /**
         * get the current lot for an asset if one exists
         */
        async getCurrentLot(asset_id)
        {
            return AssetService.getCurrentLot(asset_id)
        },

        async generateDescription(asset_id, data) {
            return AssetService.generateDescription(asset_id, data)
        },

        /**
         * export asset media
         * @param asset_id
         * @returns {Promise<obj>}
         */
        async exportMedia(asset_id) {
            return AssetService.exportMedia(asset_id)
        },

        /**
         * getter for asset media
         */
        getterAssetMedia(asset_id) {
            if (!asset_id) return null

            return this.media ? this.media['asset_' + asset_id] : {}
        },

        /**
         * get asset media
         *
         * @param {int} asset_id
         * @param {str} media_type
         */
        async getMedia(asset_id, media_type) {

            try {

                const params = []

                if (media_type) params.push({ key: 'type', value: media_type })

                const asset_media = await AssetService.getMedia(asset_id, params)

                if (asset_media) {
                    // ensure we have a holding object ready
                    this.media['asset_' + asset_id] = this.media['asset_' + asset_id] || {}
                    // this will arrive as an object with a property for each requested media type
                    for (let type in asset_media) {
                        // e.g. this.media.asset_1.image = [ ... ]
                        this.media['asset_' + asset_id][type] = asset_media[type]
                    }
                }

            } catch (error) {
                console.error('AssetService.getMedia error', error)
            }
        },

        /**
         * upddate media
         */
        async updateMedia(media_id, asset_id, data) {

            try {

                await AssetService.updateMedia(media_id, asset_id, data)

            } catch (error) {
                console.error('AssetService.updateMedia error', error)
            }
        },

        async addReplaceYouTube(asset_id, data) {
            return AssetService.addReplaceYouTube(asset_id, data)
        },

        async updateMediaSortOrders(data){
            return AssetService.updateMediaSortOrders(data)
        },

        /**
         * get asset transfer orders
         */
        async getTransferOrders(organisation_id, asset_id) {
            if (!asset_id) return null

            const transfer_orders = await AssetService.transferOrders(organisation_id, asset_id)

            if (transfer_orders) {
                this.transfer_orders['asset_' + asset_id] = transfer_orders
            }
        },

        /**
         * getter for asset transfer orders
         */
        getterAssetTransferOrders(asset_id) {
            if (!asset_id) return null

            return this.transfer_orders['asset_' + asset_id] || []
        },

        /**
         * get asset transfer requests
         */
        async getTransferRequests(organisation_id, asset_id, params) {
            if (!asset_id) return null

            const transfer_requests = await AssetService.transferRequests(organisation_id, asset_id, params)

            if (transfer_requests) {
                this.transfer_requests['asset_' + asset_id] = transfer_requests
            }
        },

        /**
         * getter for asset transfer requests
         */
        getterAssetTransferRequests(asset_id) {
            if (!asset_id) return null

            return this.transfer_requests['asset_' + asset_id] || []
        },

        /**
         * get all of the fields for an organisation's asset
         *
         * @param {int} organisation_id
         * @param {int} asset_id
         * @returns
         */
        async getFeatureFields(asset_id) {
            return AssetService.getFeatureFields(asset_id)
        },

        /**
         * archive an organisation asset
         */
        async archiveOrganisationAsset(organisation_id, asset_id) {
            return AssetService.archiveOrganisationAsset(organisation_id, asset_id)
        },

        /**
         * archive a user's asset
         */
        async archiveUserAsset(user_id, asset_id) {
            return AssetService.archiveUserAsset(user_id, asset_id)
        },

        /**
         * delete a user's asset
         */
        async deleteUserAsset(user_id, asset_id) {
            return AssetService.deleteUserAsset(user_id, asset_id)
        },

        /**
         * delete an organisation asset
         */
        async deleteOrganisationAsset(organisation_id, asset_id) {
            return AssetService.deleteOrganisationAsset(organisation_id, asset_id)
        },

        /**
         * delete all the media associated to aan asset
         * optionally filter by media type
         * @param asset_id
         * @param data
         * @returns {Promise<obj | unknown>}
         */
        async deleteAllForAsset(asset_id, data = {}) {
            return AssetService.deleteAllForAsset(asset_id, data)
        },

        /**
         * get the asset's bid interval
         */
        async getDefaultBidIncrement(asset_id) {
            return AssetService.getDefaultBidIncrement(asset_id)
        },

        async list(params) {
            return AssetService.list(params)
        },

        async assignLotNumber(asset_id, data) {
            return AssetService.assignLotNumber(asset_id, data)
        },

        async getSaleInvoices(asset_id) {
            return AssetService.getSaleInvoices(asset_id)
        },

        async detachFromSaleInvoice(asset_id, sale_invoice_id) {
            return AssetService.detachFromSaleInvoice(asset_id, sale_invoice_id)
        },

        async getSaleRemittances(asset_id) {
            return AssetService.getSaleRemittances(asset_id)
        },

        async detachFromSaleRemittance(asset_id, sale_remittance_id) {
            return AssetService.detachFromSaleRemittance(asset_id, sale_remittance_id)
        },

        async markAsSoldPaymentComplete(asset_id) {
            return AssetService.markAsSoldPaymentComplete(asset_id)
        },

        async getSaleInvoiceDefaults(asset_id, params) {
            return AssetService.getSaleInvoiceDefaults(asset_id, params)
        },

        async returnToSeller(asset_id, data) {
            return returnToSeller(asset_id, data)
        },

        async updateConnected(asset_id) {
            return AssetService.updateConnected(asset_id)
        },

        getSuggestedBidInterval(reserve) {
            let bid_interval = 50

            if (isNaN(reserve)) {
                bid_interval = 50
            } else if (reserve > 25000) {
                bid_interval = 250
            } else if (reserve > 15000) {
                bid_interval = 200
            } else if (reserve > 1000) {
                bid_interval = 100
            }

            return bid_interval;
        },

        setAssetId(asset_id) {
            this._asset_id = asset_id
        },

        clearAssetId() {
            this._asset_id = null
        },

        openAssetCreateDrawer(mode) {
            this._asset_create_drawer_open = true
            this._asset_create_drawer_mode = mode || null
        },

        closeAssetCreateDrawer() {
            this._asset_create_drawer_open = false
            this._asset_create_drawer_mode = null
        }

    }

})
